import React, { useMemo } from 'react'
import { SubmitHandler, useFormContext } from 'react-hook-form'
import FilterList from '@/components/content/FilterList'
import DateRangePickerInput from '@/components/date/DateRangePickerInput'
import MultipleInput from '@/components/content/MultipleInput'
import { GetOrderOperationHistoriesReq } from '@/services/outbound/outboundUnallocated/types'
import { UNALLOCATED_SEARCH_MAX_DATE_RANGE, UNALLOCATED_SEARCH_MAX_INPUT_SIZE } from '@/services/outbound/outboundUnallocated/consts'
import { DATE_FORMAT } from '@/utils/consts'
import { GetOrdersReq } from '@/services/outbound/outboundWaitingStatus/types'
import { chunk, toArray } from 'lodash-es'
import dayjs from 'dayjs'
import ShipperSelector from '@/components/system/ShipperSelector'
import { SHIPPER_TYPES } from '@/services/shipper/apis'

export const getInitForm = (parsedQs: Partial<GetOrderOperationHistoriesReq> = {}): Partial<GetOrderOperationHistoriesReq> => ({
  shipperNames: [],
  logisticsServiceTypes: parsedQs.logisticsServiceTypes
    ? Array.isArray(parsedQs.logisticsServiceTypes)
      ? parsedQs.logisticsServiceTypes
      : [parsedQs.logisticsServiceTypes]
    : SHIPPER_TYPES,
  collectedDateFrom: parsedQs.collectedDateFrom || dayjs().format(DATE_FORMAT.date),
  collectedDateTo: parsedQs.collectedDateTo || dayjs().format(DATE_FORMAT.date),
  orderNumber: toArray(parsedQs.orderNumber),
  orderOptionNumber: toArray(parsedQs.orderOptionNumber),
  barcode: toArray(parsedQs.barcode),

  // TODO: [SCOPE B] 추가 예정
  // optionCode:parsedQs.optionCode || undefined,
  // unallocatedAction: parsedQs.unallocatedAction || undefined,
})

interface Props {
  isLoading: boolean
  onSubmit: SubmitHandler<GetOrdersReq>
  onFieldChange?: (params: { fieldName: keyof GetOrdersReq; value: unknown; isSelectedAll?: boolean }) => void
  onReset?: () => void
}

function OutboundUnallocatedForm({ isLoading, onSubmit, onFieldChange, onReset }: Props) {
  const { setValue, watch, reset, handleSubmit } = useFormContext<GetOrdersReq>()
  const values = watch()

  const filterList = useMemo(
    () =>
      chunk(
        [
          {
            label: '화주명',
            required: true,
            validationValue: values.shipperNames && values.shipperNames.length > 0,
            value: <ShipperSelector logisticsServiceTypesWidth={120} shipperNamesWidth={250} onFieldChange={onFieldChange} />,
          },
          {
            label: '주문수집일',
            required: true,
            value: (
              <DateRangePickerInput
                value={[values.collectedDateFrom, values.collectedDateTo]}
                maxDateRange={UNALLOCATED_SEARCH_MAX_DATE_RANGE}
                onChange={([start, end]) => {
                  setValue('collectedDateFrom', start ? dayjs(start).format(DATE_FORMAT.date) : '')
                  setValue('collectedDateTo', end ? dayjs(end).format(DATE_FORMAT.date) : '')
                }}
              />
            ),
          },
          {
            label: '주문번호',
            value: (
              <MultipleInput modalTitle="주문번호" value={values.orderNumbers ?? []} onChange={(v) => setValue('orderNumbers', v)} maxSize={UNALLOCATED_SEARCH_MAX_INPUT_SIZE} />
            ),
          },
          {
            label: '주문일련번호',
            value: (
              <MultipleInput
                modalTitle="주문일련번호"
                value={values.orderOptionNumbers ?? []}
                onChange={(v) => setValue('orderOptionNumbers', v)}
                maxSize={UNALLOCATED_SEARCH_MAX_INPUT_SIZE}
              />
            ),
          },
          {
            label: '바코드',
            value: <MultipleInput modalTitle="바코드" value={values.barcodes ?? []} onChange={(v) => setValue('barcodes', v)} maxSize={UNALLOCATED_SEARCH_MAX_INPUT_SIZE} />,
          },
          // TODO: [SCOPE B] 추가 예정
          // {
          //   label: '옵션코드',
          //   value: (
          //     <MultipleInput modalTitle="옵션코드" value={values.optionCode ?? []} onChange={(v) => setValue('optionCode', v)} maxSize={200} />
          //   ),
          // },
          // {
          //   label: '미할당처리결과',
          //   value: <UnallocatedResultSelect value={values.unallocatedAction} onChange={(v) => setValue('unallocatedAction', v)} />,
          // },
        ],
        3,
      ),
    [values],
  )

  return (
    <FilterList
      isLoading={isLoading}
      list={filterList}
      onReset={onReset || (() => reset({ ...getInitForm() }))}
      onSearch={handleSubmit(onSubmit)}
    />
  )
}

export default OutboundUnallocatedForm
